import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  MessageCircle,
  Paintbrush,
  Car,
  Shield,
  Sparkles,
  Clock,
  MapPin,
  ChevronRight,
  ChevronDown,
  Star,
  Menu,
  X,
} from 'lucide-react';
import logo from '@/assets/logo.png';

const PHONE_1 = '013-5256 876';
const PHONE_2 = '014-330 9987';
const WHATSAPP = '60135256876';
const ADDRESS = 'LOT 4027-A, JALAN 1D, KAMPUNG BARU SUBANG, SEKSYEN U6, 40150 SHAH ALAM, SELANGOR';
const MAPS_EMBED =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3984.0!2d101.58!3d3.08!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sShah+Alam!5e0!3m2!1sen!2smy!4v1700000000000';

const SERVICES = [
  {
    icon: Paintbrush,
    title: 'Full Body Repaint',
    titleMs: 'Cat Semula Seluruh Badan',
    desc: 'Complete respray with premium automotive paint for a factory-fresh finish.',
  },
  {
    icon: Car,
    title: 'Panel Painting',
    titleMs: 'Cat Panel',
    desc: 'Targeted panel repair and painting for dents, scratches, and collision damage.',
  },
  {
    icon: Sparkles,
    title: 'Custom & Special Effects',
    titleMs: 'Kesan Khas & Custom',
    desc: 'Matte, metallic, candy, and chameleon finishes to make your car stand out.',
  },
  {
    icon: Shield,
    title: 'Minor Repair & Touch-Up',
    titleMs: 'Pembaikan Kecil',
    desc: 'Quick fixes for stone chips, key scratches, and minor paint defects.',
  },
];

const HOURS = [
  { day: 'Monday - Friday', time: '9:00 AM - 6:00 PM' },
  { day: 'Saturday', time: '9:00 AM - 2:00 PM' },
  { day: 'Sunday & Public Holiday', time: 'Closed' },
];

const NAV_LINKS = [
  { label: 'Services', href: '#services' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Colors', href: '/colors', isRoute: true },
  { label: 'Contact', href: '#contact' },
];

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* ── 1. Navbar ──────────────────────────────────────────── */}
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/80 backdrop-blur-md border-b border-white/10' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Bro Autopaint Garage logo"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className="font-bold text-sm tracking-wide hidden sm:inline">
              BRO AUTOPAINT GARAGE
            </span>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ),
            )}
            <Link
              to="/login"
              className="ml-2 px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
            >
              Login
            </Link>
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden p-2 text-zinc-300 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500 rounded-md"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu overlay */}
        {menuOpen && (
          <div className="md:hidden bg-zinc-950/95 backdrop-blur-lg border-t border-white/10 absolute inset-x-0 top-16 bottom-0 h-[calc(100vh-4rem)]">
            <div className="flex flex-col p-6 gap-2">
              {NAV_LINKS.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="px-4 py-3 text-lg text-zinc-300 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="px-4 py-3 text-lg text-zinc-300 hover:text-white transition-colors rounded-lg hover:bg-white/5"
                  >
                    {link.label}
                  </a>
                ),
              )}
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="px-4 py-3 text-lg text-zinc-300 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              >
                Login
              </Link>
              <a
                href={`https://wa.me/${WHATSAPP}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-orange-500 text-white font-semibold text-lg"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp Us
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ── 2. Hero ────────────────────────────────────────────── */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="/20260314_063440910_iOS.jpg"
          alt="Glossy black Mercedes after professional respray"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/30" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-400 text-xs tracking-widest uppercase">
            Shah Alam, Selangor
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-4 tracking-tight">
            Transform Your Ride.
            <br />
            <span className="text-orange-500">Perfectly.</span>
          </h1>
          <p className="text-zinc-400 text-lg sm:text-xl max-w-2xl mx-auto mb-10">
            Cat kereta berkualiti tinggi dengan jaminan. Dipercayai ratusan pelanggan di Selangor.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-orange-500 text-white font-bold text-lg hover:bg-orange-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
            >
              <MessageCircle className="h-5 w-5" />
              Get a Free Quote
            </a>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border border-white/20 text-white font-semibold text-lg hover:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
            >
              See Services
              <ChevronRight className="h-5 w-5" />
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <ChevronDown className="h-6 w-6 text-zinc-400 animate-bounce" />
        </div>
      </section>

      {/* ── 3. Stats / Trust Bar ───────────────────────────────── */}
      <section className="bg-zinc-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '2016', label: 'Established', labelMs: 'Ditubuhkan' },
              { value: '1,000+', label: 'Cars Painted', labelMs: 'Kereta Dicat' },
              { value: 'Shah Alam', label: 'Selangor', labelMs: 'Lokasi Kami' },
              { value: '5.0', label: '5-Star Rating', labelMs: 'Penarafan Bintang', isStar: true },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="flex items-center justify-center gap-1">
                  <span className="text-3xl sm:text-4xl font-bold text-orange-500">
                    {stat.value}
                  </span>
                  {stat.isStar && <Star className="h-5 w-5 text-orange-500 fill-orange-500" />}
                </div>
                <p className="text-sm text-zinc-300 mt-1">{stat.label}</p>
                <p className="text-xs text-zinc-500">{stat.labelMs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. Services ────────────────────────────────────────── */}
      <section id="services" className="py-20 sm:py-28 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-2">Our Services</h2>
          <p className="text-zinc-500 text-center mb-14 text-sm">Perkhidmatan Kami</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                className="group bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-orange-500/50 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="h-12 w-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-5 group-hover:bg-orange-500/20 transition-colors">
                  <s.icon className="h-6 w-6 text-orange-500" />
                </div>
                <h3 className="font-semibold text-lg mb-0.5">{s.title}</h3>
                <p className="text-xs text-zinc-500 mb-3">{s.titleMs}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. Gallery Preview ─────────────────────────────────── */}
      <section id="gallery" className="py-20 sm:py-28 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-2">Our Work</h2>
          <p className="text-zinc-500 text-center mb-14 text-sm">Hasil Kerja Kami</p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Large image */}
            <div className="overflow-hidden rounded-2xl aspect-[4/3] md:row-span-2">
              <img
                src="/20260314_063440910_iOS.jpg"
                alt="Glossy black Mercedes — full body respray"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            {/* Smaller images */}
            <div className="overflow-hidden rounded-2xl aspect-[4/3]">
              <img
                src="/20260314_063206648_iOS.jpg"
                alt="Blue Toyota after panel painting"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="overflow-hidden rounded-2xl aspect-[4/3]">
              <img
                src="/20260314_063552299_iOS.jpg"
                alt="Green Honda City with fresh paint"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
            >
              View Full Gallery
              <ChevronRight className="h-4 w-4" />
            </Link>
            <Link
              to="/colors"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-zinc-700 text-white font-semibold hover:bg-white/5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500"
            >
              <Sparkles className="h-4 w-4 text-orange-500" />
              Browse Color Catalog
            </Link>
          </div>
        </div>
      </section>

      {/* ── 6. Workshop / About ────────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Photo stack */}
            <div className="relative">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src="/20260314_063412377_iOS.jpg"
                  alt="Bro Autopaint Garage workshop interior — busy day"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Inset photo */}
              <div className="absolute -bottom-6 -right-2 sm:right-6 w-40 sm:w-52 rounded-xl overflow-hidden border-4 border-zinc-950 shadow-2xl">
                <img
                  src="/20260314_063259858_iOS.jpg"
                  alt="Technician working on a Honda CR-V"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Badge */}
              <div className="absolute top-4 left-4 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg">
                Est. 2016
              </div>
            </div>

            {/* Text */}
            <div className="pt-8 lg:pt-0">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Trusted Workshop
                <br />
                <span className="text-orange-500">in Shah Alam</span>
              </h2>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                Since 2016, Bro Autopaint Garage has delivered premium car painting services to
                thousands of satisfied customers across Selangor. Our team combines skilled
                craftsmanship with modern equipment to achieve showroom-quality results.
              </p>
              <ul className="space-y-3">
                {[
                  'Experienced painters with 10+ years in the industry',
                  'Premium automotive paint brands only',
                  'Full dust-free spray booth facility',
                  'Warranty on every paint job',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-zinc-300 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Full-width exterior shot */}
          <div className="mt-16 overflow-hidden rounded-2xl">
            <img
              src="/20260314_063357877_iOS.jpg"
              alt="Bro Autopaint Garage exterior — workshop scale"
              className="w-full h-64 sm:h-80 lg:h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* ── 7. Testimonials / Social Proof ─────────────────────── */}
      <section className="py-20 sm:py-28 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Testimonial cards */}
            <div className="relative">
              {/* Decorative quote */}
              <span className="absolute -top-8 -left-4 text-[120px] leading-none text-orange-500/10 font-serif select-none pointer-events-none">
                &ldquo;
              </span>

              <div className="space-y-6">
                {[
                  {
                    name: 'Ahmad R.',
                    car: 'Perodua Myvi',
                    text: 'Cat kereta macam baru! Siap on time, harga berpatutan. Memang recommend Bro Autopaint.',
                    stars: 5,
                  },
                  {
                    name: 'Siti N.',
                    car: 'Honda City',
                    text: 'First time cat kereta sini, memang puas hati. Colour matching perfect, staff pun friendly.',
                    stars: 5,
                  },
                ].map((t) => (
                  <div
                    key={t.name}
                    className="bg-zinc-800/50 border border-zinc-700/50 rounded-xl p-6"
                  >
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-orange-500 fill-orange-500" />
                      ))}
                    </div>
                    <p className="text-zinc-300 text-sm leading-relaxed mb-4">
                      &ldquo;{t.text}&rdquo;
                    </p>
                    <div>
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-zinc-500">{t.car}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer collage photo */}
            <div className="overflow-hidden rounded-2xl">
              <img
                src="/20260314_063105696_iOS.jpg"
                alt="Happy customers and completed paint jobs"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Contact ─────────────────────────────────────────── */}
      <section id="contact" className="py-20 sm:py-28 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-2">Get In Touch</h2>
          <p className="text-zinc-500 text-center mb-14 text-sm">Hubungi Kami</p>

          {/* Info strip */}
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <Phone className="h-6 w-6 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Phone</h3>
              <a
                href={`tel:${PHONE_1.replace(/[^0-9+]/g, '')}`}
                className="block text-sm text-zinc-400 hover:text-white transition-colors"
              >
                {PHONE_1}
              </a>
              <a
                href={`tel:${PHONE_2.replace(/[^0-9+]/g, '')}`}
                className="block text-sm text-zinc-400 hover:text-white transition-colors"
              >
                {PHONE_2}
              </a>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <MapPin className="h-6 w-6 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Location</h3>
              <p className="text-sm text-zinc-400">{ADDRESS}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
              <Clock className="h-6 w-6 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Business Hours</h3>
              <div className="space-y-1">
                {HOURS.map((h) => (
                  <div key={h.day} className="flex justify-between text-sm gap-2">
                    <span className="text-zinc-400">{h.day}</span>
                    <span
                      className={
                        h.time === 'Closed'
                          ? 'text-red-400 font-medium'
                          : 'text-zinc-300 font-medium'
                      }
                    >
                      {h.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Map + CTA */}
          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-zinc-800 h-72 lg:h-auto min-h-[300px]">
              <iframe
                src={MAPS_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Workshop Location"
              />
            </div>
            <div className="lg:col-span-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
              <MessageCircle className="h-10 w-10 mb-4" />
              <h3 className="text-2xl font-bold mb-2">Ready to Transform Your Car?</h3>
              <p className="text-orange-100 text-sm mb-6">
                Dapatkan sebut harga percuma melalui WhatsApp. Balas dalam masa 30 minit.
              </p>
              <a
                href={`https://wa.me/${WHATSAPP}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-white text-orange-600 font-bold text-lg hover:bg-orange-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. Footer ──────────────────────────────────────────── */}
      <footer className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid sm:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={logo} alt="Logo" className="h-8 w-8 rounded-lg object-cover" />
                <span className="font-bold text-sm tracking-wide">BRO AUTOPAINT GARAGE</span>
              </div>
              <p className="text-sm text-zinc-500 leading-relaxed">
                Professional car painting services in Shah Alam, Selangor. Quality you can see,
                service you can trust.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="font-semibold text-sm mb-4 text-zinc-400 uppercase tracking-wider">
                Quick Links
              </h4>
              <div className="space-y-2">
                <a
                  href="#services"
                  className="block text-sm text-zinc-500 hover:text-white transition-colors"
                >
                  Services
                </a>
                <Link
                  to="/gallery"
                  className="block text-sm text-zinc-500 hover:text-white transition-colors"
                >
                  Gallery
                </Link>
                <Link
                  to="/colors"
                  className="block text-sm text-zinc-500 hover:text-white transition-colors"
                >
                  Colors
                </Link>
                <Link
                  to="/login"
                  className="block text-sm text-zinc-500 hover:text-white transition-colors"
                >
                  Login
                </Link>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-sm mb-4 text-zinc-400 uppercase tracking-wider">
                Contact
              </h4>
              <div className="space-y-2 text-sm text-zinc-500">
                <a
                  href={`tel:${PHONE_1.replace(/[^0-9+]/g, '')}`}
                  className="block hover:text-white transition-colors"
                >
                  {PHONE_1}
                </a>
                <a
                  href={`tel:${PHONE_2.replace(/[^0-9+]/g, '')}`}
                  className="block hover:text-white transition-colors"
                >
                  {PHONE_2}
                </a>
                <p className="leading-relaxed">{ADDRESS}</p>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-zinc-800 text-center text-xs text-zinc-600">
            &copy; {new Date().getFullYear()} Bro Autopaint Garage (SA0379528-K). All rights
            reserved.
          </div>
        </div>
      </footer>

      {/* ── Fixed Mobile WhatsApp Bar ──────────────────────────── */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-zinc-950/95 backdrop-blur-sm border-t border-zinc-800 p-3">
        <a
          href={`https://wa.me/${WHATSAPP}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-orange-500 text-white font-bold hover:bg-orange-600 transition-colors"
        >
          <MessageCircle className="h-5 w-5" />
          WhatsApp Us Now
        </a>
      </div>
    </div>
  );
}
